import { DocumentNode, gql, useMutation } from '@apollo/client';
import { MenuItem, useDisclosure, useToast } from '@chakra-ui/react';
import * as React from 'react';

import {
  EModItemResolutionState,
  EModerationReason,
} from '@/client/legacy-shared-modules/moderation-item/moderationType';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { useSignUpPrompt } from '@/hooks/useSignUpPrompt';
import { FlagIcon } from '@/imports/ui/chakra/feather';

import {
  ReportPostDropdownItem_CreateModerationItem,
  ReportPostDropdownItem_CreateModerationItemVariables,
} from './graphql-types/ReportPostDropdownItem_CreateModerationItem';
import { ReportPostDropdownItem_PostFragment } from './graphql-types/ReportPostDropdownItem_PostFragment';

import { useUpdateCacheOnPostNotVisible } from '../hooks/useUpdateCacheOnPostNotVisible';

const POST_FRAGMENT = gql`
  fragment ReportPostDropdownItem_PostFragment on PostV2 {
    _id
    ...UseUpdateCacheOnPostNotVisible_PostV2Fragment
    moderationItem {
      _id
      resolutionState
    }
  }
  ${useUpdateCacheOnPostNotVisible.postFragment}
`;

const CREATE_MODERATION_ITEM = gql`
  mutation ReportPostDropdownItem_CreateModerationItem(
    $input: CreateModerationItemInputV2!
  ) {
    createModerationItemV2(input: $input) {
      data {
        _id
        postV2 {
          _id
          currentUserCanUpdatePost
          currentUserCanRespondToPost
          currentUserCanEditAnnouncedState
          currentUserCanResolveModeratedState
          ...ReportPostDropdownItem_PostFragment
        }
      }
    }
  }
  ${POST_FRAGMENT}
`;

export const ReportPostDropdownItem: React.FC<{
  post: ReportPostDropdownItem_PostFragment;
}> & { postFragment: DocumentNode } = ({ post }) => {
  const toast = useToast();
  const confirmDialog = useDisclosure();
  const { promptModal, withPrompt } = useSignUpPrompt();
  const { updateCacheOnPostNotVisible } = useUpdateCacheOnPostNotVisible();

  const [createModerationItem] = useMutation<
    ReportPostDropdownItem_CreateModerationItem,
    ReportPostDropdownItem_CreateModerationItemVariables
  >(CREATE_MODERATION_ITEM, {
    onCompleted: () => {
      toast({
        title: `This post has been reported.`,
        status: 'success',
      });
    },
    onError: () => {
      toast({
        title: `Failed to report post.`,
        status: 'error',
      });
    },
    update: (cache, result) => {
      const canUserViewModeratedPost =
        !!result.data?.createModerationItemV2?.data?.postV2;

      // Remove the post from all caches if the user can no longer view it
      if (!canUserViewModeratedPost) {
        updateCacheOnPostNotVisible({ cache, post });
      }
    },
  });

  const reportPostAsync = async (): Promise<void> => {
    await createModerationItem({
      variables: {
        input: {
          data: {
            resolutionState: EModItemResolutionState.PENDING,
            reason: EModerationReason.Reported,
            postId: post._id,
          },
        },
      },
    });
  };

  return (
    <>
      <MenuItem icon={<FlagIcon />} onClick={withPrompt(confirmDialog.onOpen)}>
        Report this Post
      </MenuItem>
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        onClose={confirmDialog.onClose}
        title="Report this Post"
        body="Reporting this post will hide it from other community members until it is reviewed by a community admin. Are you sure you want to report this post?"
        confirmButtonText="Report Post"
        onConfirm={reportPostAsync}
      />
      {promptModal}
    </>
  );
};

ReportPostDropdownItem.postFragment = POST_FRAGMENT;
